/* eslint-disable no-console */
import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from 'modules/hooks/appHooks';
import { useToastContainer } from 'modules/layout/toastContainer';
import { ProfileSelectors, ProfileThunks } from 'modules/profilePage/redux';
import { InputWithLabel } from 'modules/ui';
import { FormButtons, FormContainer } from 'modules/ui/form';
import { ToastType } from 'modules/ui/toast';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ValidationType } from '../../../../types/ValidationType';
import { useForm } from '../../../ui/form/useForm';
import { passwordResetFinished } from '../../redux/slice';
import { PasswordContainer } from './styled';

export const PasswordResetForm: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToastContainer();

  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useQuery();

  const [validate, setValidate] = useState<boolean>(false);
  const { validateForm } = useForm();
  const passwordResetLoading = useAppSelector(ProfileSelectors.selectLoading);
  const passwordResetSuccess = useAppSelector(
    ProfileSelectors.selectPasswordResetSuccess,
  );
  const passwordResetError = useAppSelector(
    ProfileSelectors.selectPasswordResetError,
  );

  const [newPassword, setNewPassword] = useState<string>('');
  const [repeatedPassword, setRepeatedPassword] = useState<string>('');

  const [hasErrors, setHasErrors] = useState<boolean>(true);
  const [buttonClicked, setButtonClicked] = useState(false);

  useEffect(() => {
    if (passwordResetLoading === false) {
      if (!passwordResetError && passwordResetSuccess) {
        setNewPassword('');
        setRepeatedPassword('');
        addToast({
          title: t('profilePage.passwordReset.successTitle'),
          text: t('profilePage.passwordReset.successMessage'),
        });
        dispatch(passwordResetFinished());
      } else if (passwordResetError && !passwordResetSuccess) {
        setNewPassword('');
        setRepeatedPassword('');
        addToast({
          title: t('profilePage.passwordReset.errorTitle'),
          text: t('profilePage.passwordReset.errorMessage'),
          type: ToastType.Urgent,
        });
      }
    }
  }, [
    addToast,
    history,
    passwordResetError,
    passwordResetLoading,
    passwordResetSuccess,
    dispatch,
    t,
  ]);

  const repeatedPasswordErrorText = useMemo(() => {
    if (!validate) return undefined;

    return newPassword?.length && repeatedPassword !== newPassword
      ? t('profilePage.passwordReset.repeatPasswordErrorText')
      : '';
  }, [repeatedPassword, newPassword, t, validate]);

  const handlePasswordReset = useCallback(
    (event: any) => {
      event.preventDefault();
      setValidate(true);
      setHasErrors(validateForm(event));
      setButtonClicked(true);
    },
    [validateForm],
  );

  useEffect(() => {
    if (
      buttonClicked &&
      validate &&
      !hasErrors &&
      repeatedPasswordErrorText?.length === 0
    ) {
      dispatch(
        ProfileThunks.resetPassword({
          resetToken: query.get('token'),
          newPassword,
        }),
      );
      setValidate(false);
    }

    setButtonClicked(false);
  }, [
    dispatch,
    repeatedPasswordErrorText,
    hasErrors,
    buttonClicked,
    validate,
    newPassword,
    query,
  ]);

  return (
    <FormContainer noValidate onSubmit={handlePasswordReset}>
      <PasswordContainer>
        <InputWithLabel
          required
          defaultValue={newPassword}
          label={t('profilePage.passwordReset.newPasswordLabel')}
          labelTooltip={t('profilePage.passwordReset.passwordTooltip')}
          onBlur={(e) => setNewPassword(e.target.value)}
          placeholderText={t('profilePage.passwordReset.passwordPlaceholder')}
          type="password"
          validators={[ValidationType.PASSWORD]}
          validate={validate}
        />
        {/* TODO: popraviti uvjete za kvacice */}

        {/* <StyledSuccessIconContainer visible={!hasErrors}> */}
        {/*  <NavigationAndActionSuccessSmall /> */}
        {/* </StyledSuccessIconContainer> */}
      </PasswordContainer>

      <PasswordContainer>
        <InputWithLabel
          required
          defaultValue={repeatedPassword}
          onChange={(e) => setRepeatedPassword(e.target.value)}
          label={t('profilePage.passwordReset.repeatPasswordLabel')}
          placeholderText={t('profilePage.passwordReset.passwordPlaceholder')}
          type="password"
          errorText={repeatedPasswordErrorText}
        />
        {/* TODO: popraviti uvjete za kvacice */}

        {/* <StyledSuccessIconContainer */}
        {/*  visible={!!newPassword.length && repeatedPassword === newPassword}> */}
        {/*  <NavigationAndActionSuccessSmall /> */}
        {/* </StyledSuccessIconContainer> */}
      </PasswordContainer>

      <FormButtons
        text={t('profilePage.passwordReset.createPasswordButtonText')}
      />
    </FormContainer>
  );
};
